import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { GenericDialog } from "@/components/dialogs";
import { highlight } from "@/util/Highlighter";
import { Project } from "@/api/project";
import { ProjectFilters } from "../components/Filters";
import { useDebounce } from "@/hooks";
import { useProjectLookup } from "../hooks";
import { useState } from "react";

import styles from "./SelectProject.tsx.module.css";

export type SelectProjectDialogProps = {
  initialProject?: Project;
  onAccept: (project: Project) => void;
};

export function SelectProjectDialog(props: SelectProjectDialogProps) {
  const [selectedProject, setSelectedProject] = useState(props.initialProject);
  const [showFilters, setShowFilters] = useState(false);
  const [search, setSearch] = useState("");

  const debouncedSearch = useDebounce(search, 300);
  const projects = useProjectLookup(debouncedSearch);

  function handleSelectProject() {
    if (selectedProject) {
      props.onAccept(selectedProject);
    }
  }

  return (
    <GenericDialog
      caption="Select Project"
      buttons="OKCancel"
      className={styles.dialog}
      allowAccept={Boolean(selectedProject)}
      onAccept={handleSelectProject}
    >
      <>
        <div className="d-flex justify-content-between">
          <div className="input-group" style={{ maxWidth: 400 }}>
            <input
              type="text"
              className="form-control"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
            />
            <span className="input-group-text" role="button" onClick={() => projects.refetch()}>
              <FontAwesomeIcon size="sm" icon={faMagnifyingGlass} title="Search" />
            </span>
          </div>
          <div className="form-check form-check-reverse form-switch my-auto">
            <label htmlFor="filters" className="ms-3">
              Filters
            </label>
            <input
              name="filters"
              type="checkbox"
              className="form-check-input"
              checked={showFilters}
              onChange={(e) => setShowFilters(e.currentTarget.checked)}
            />
          </div>
        </div>

        <div className={styles.container}>
          {showFilters ? (
            <div style={{ maxWidth: 400 }}>
              <ProjectFilters />
            </div>
          ) : (
            <>
              {projects.isLoading ? (
                <p>Loading...</p>
              ) : projects.isError ? (
                <p>An unknown error occurred.</p>
              ) : debouncedSearch && projects.data?.length === 0 ? (
                <p>Your search returned no results.</p>
              ) : projects.data?.length === 0 ? (
                <p>No projects found.</p>
              ) : (
                <table className="table table-sm table-borderless table-hover">
                  <thead className="table-light">
                    <tr>
                      <th>Name</th>
                      <th className="d-none d-md-table-cell">Number</th>
                      <th className="d-none d-md-table-cell">Description</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {projects.data?.map((project) => (
                      <tr key={project.id} onClick={() => setSelectedProject(project)} className="cursor-pointer">
                        <td>{highlight(project.name, debouncedSearch)}</td>
                        <td className="d-none d-md-table-cell">{highlight(project.number, debouncedSearch)}</td>
                        <td className="d-none d-md-table-cell">{highlight(project.description, debouncedSearch)}</td>
                        <td className="text-end pe-3">
                          <input
                            type="radio"
                            className="form-check-input"
                            checked={project.id === selectedProject?.id}
                            onChange={() => setSelectedProject(project)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </>
    </GenericDialog>
  );
}
